import { Autocomplete, Box, Checkbox, FormControlLabel, Paper, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import DatePicker from "../../../components/common/DatePicker";
import Subheading from "../../../components/common/Subheading";
import Subtitle from "../../../components/common/Subtitle";

/**
 * DataPreparation component for the Auto4Client feature.
 *
 * This component renders a form for data preparation inputs, including company selection and date range selection.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Array<string>} props.companyNames - Array of company names available for selection.
 * @param {Function} props.onInputsChange - Callback function to notify parent about state changes.
 *
 * @returns {JSX.Element} - The JSX element for the data preparation form.
 */
function DataPreparation({ companyNames, onInputsChange }) {
  const [inputs, setInputs] = useState({
    allCompaniesSelected: true,
    selectedCompany: null,
    startDate: dayjs().subtract(1, "day"),
    endDate: dayjs(),
  });

  // Notify parent component when state changes
  useEffect(() => {
    onInputsChange(inputs);
  }, [inputs, onInputsChange]);

  const toggleAllCompanies = () => {
    const newVal = !inputs.allCompaniesSelected;
    if (newVal === true) {
      setInputs({ ...inputs, selectedCompany: null, allCompaniesSelected: newVal });
    } else {
      setInputs({ ...inputs, allCompaniesSelected: newVal });
    }
  };

  const handleSelectCompany = (event, value) => setInputs({ ...inputs, selectedCompany: value });

  const handleDateChange = (field) => (val) => {
    setInputs((prevState) => ({
      ...prevState,
      [field]: val,
    }));
  };

  return (
    <Grid container direction="column" spacing={4} p={3} mx={"10%"}>
      {/* Company Selection Section */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Subheading text="Company Selection" sx={{ mb: 3 }} />
        <FormControlLabel
          control={<Checkbox checked={inputs.allCompaniesSelected} onChange={toggleAllCompanies} />}
          label="Select All Companies"
          sx={{ mb: 2 }}
        />
        {!inputs.allCompaniesSelected && (
          <Autocomplete
            options={companyNames}
            renderInput={(params) => <TextField {...params} label="Select Company" />}
            onChange={handleSelectCompany}
            fullWidth
            size="medium"
          />
        )}
      </Paper>

      {/* Date Range Section */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Subheading text="Date Range" sx={{ mb: 3 }} />
        <Grid container spacing={3} justifyContent="center">
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box
              flexDirection="column"
              borderRadius="15px"
              p={1}
              sx={{ backgroundColor: "#121212", display: "flex", justifyContent: "center", textAlign: "center" }}
            >
              <Subtitle text="Start Date" sx={{ fontSize: "1.15rem", py: 1 }} />
              <DatePicker currentVal={inputs.startDate} handleVal={handleDateChange("startDate")} />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ textAlign: "center" }}>
            <Box
              flexDirection="column"
              borderRadius="15px"
              p={1}
              sx={{ backgroundColor: "#121212", display: "flex", justifyContent: "center" }}
            >
              <Subtitle text="End Date" sx={{ fontSize: "1.15rem", py: 1 }} />
              <DatePicker currentVal={inputs.endDate} handleVal={handleDateChange("endDate")} />
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
}

export default DataPreparation;
